import { childAgeMatchesClass } from "helpers/dataConversion/convertClassAgeRange";

export const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export const DAY_LABELS = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

export const TIME_OF_DAY_OPTIONS = [
  { id: "any", label: "Anytime" },
  { id: "morning", label: "Before noon" },
  { id: "evening", label: "After 3 pm" },
];

export const DEFAULT_CLASS_SCHEDULE_FILTER = {
  days: [],
  timeOfDay: "any",
  childAgeYears: null,
  childAgeMonths: 0,
  openSpotsOnly: false,
};

/** @returns {boolean} */
export const isClassScheduleFilterActive = (filter) => {
  if (!filter) return false;
  if (filter.openSpotsOnly) return true;
  if (filter.childAgeYears !== null && filter.childAgeYears !== "") return true;
  if (filter.timeOfDay && filter.timeOfDay !== "any") return true;
  return Array.isArray(filter.days) && filter.days.length > 0;
};

/** Parse "15:30" to minutes since midnight. Returns null if invalid. */
export const parseTimeToMinutes = (timeString) => {
  if (!timeString || typeof timeString !== "string") return null;
  const [hourPart, minutePart] = timeString.split(":");
  const hours = Number(hourPart);
  const minutes = Number(minutePart);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
};

/** @returns {boolean} */
export const classMatchesTimeOfDay = (startTime, timeOfDay) => {
  if (!timeOfDay || timeOfDay === "any") return true;

  const minutes = parseTimeToMinutes(startTime);
  if (minutes === null) return false;

  switch (timeOfDay) {
    case "morning":
      return minutes < 12 * 60;
    case "evening":
      return minutes >= 15 * 60;
    default:
      return true;
  }
};

/** @returns {boolean} True when the class meets on any selected day. */
export const classMatchesSelectedDays = (meetingDays, selectedDays) => {
  if (!selectedDays?.length) return true;

  const meeting = meetingDays || {};
  return selectedDays.some((day) => meeting[day]);
};

/** @returns {boolean} */
export const classRowMatchesFilter = (row, filter) => {
  if (!isClassScheduleFilterActive(filter)) return true;

  const openings = row.openings?.calculated_openings ?? 0;
  if (filter.openSpotsOnly && openings <= 0) return false;

  if (!classMatchesSelectedDays(row.meeting_days, filter.days || [])) {
    return false;
  }

  if (!classMatchesTimeOfDay(row.start_time, filter.timeOfDay)) {
    return false;
  }

  if (
    !childAgeMatchesClass(
      filter.childAgeYears,
      filter.childAgeMonths,
      row.min_age,
      row.max_age
    )
  ) {
    return false;
  }

  return true;
};

/**
 * Filter rows to exact matches only, sorted by class name.
 * @returns {{ rows: Array, matchCount: number }}
 */
export const filterAndSortClassRows = (rows, filter) => {
  const source = Array.isArray(rows) ? rows : [];

  if (!isClassScheduleFilterActive(filter)) {
    return {
      rows: source,
      matchCount: source.length,
    };
  }

  const filtered = source
    .filter((row) => classRowMatchesFilter(row, filter))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  return {
    rows: filtered,
    matchCount: filtered.length,
  };
};

export const summarizeFilterResults = (sectionCounts) => {
  return Object.values(sectionCounts || {}).reduce(
    (acc, counts) => ({
      matchCount: acc.matchCount + (counts.matchCount || 0),
      sourceCount: acc.sourceCount + (counts.sourceCount || 0),
    }),
    { matchCount: 0, sourceCount: 0 }
  );
};
